/**
 * Resolve the app's RUN folder — the id `HtmlEditor` feeds to `HtmlViewer` as `parentId`,
 * which in turn drives the Papan-bar embed's `folderId`, the consent / `whoami` relay, and
 * `scripts.run`. It must be the folder the app is actually SERVED from (where its
 * `manifest.json` / `_scripts/` live), NOT the HTML file's raw DB parent.
 *
 * The two diverge for:
 *   - a mounted `.shared` app — the file's `parentId` flattens to the MOUNT ROOT (the
 *     `.shared` pointer at the *project* folder), not the app's own subfolder; and
 *   - a subfolder-entry app — HTML at `<root>/pages/index.html` referencing `../lib.js`
 *     is served rooted at `<root>`, while its DB parent is `<root>/pages`.
 * In both cases the file-parent is wrong; the preview session resolves the true served
 * root server-side (`sessionRootId`). Keying the SDK relay on the file-parent instead
 * makes consent/`whoami` resolve to the wrong folder (no manifest → 400 "Manage
 * permissions" / "no identity") and `scripts.run` look in the wrong place.
 *
 * The share-view path already threads the correct root via `parentId`
 * (`previewSessionIdOverride` set), so it is returned verbatim there. Before the session
 * resolves (`sessionRootId` null) it falls back to the file-parent; in practice the app
 * `HtmlViewer` only renders once the session is established, so the fallback is a guard,
 * not a live transient.
 */
export function deriveAppRunFolderId(args: {
  /** Non-null in the SHARE-VIEW path; `parentId` there is already the session's app-run root. */
  previewSessionIdOverride: string | null | undefined
  /** The HTML file's resolved DB parent (or the share-threaded root when overridden). */
  resolvedParentId: string | null
  /** The preview session's server-resolved run root (`currentRoot.rootId`); null until resolved. */
  sessionRootId: string | null | undefined
}): string | null {
  // Share-view: `resolvedParentId` is already the session's app-run root (threaded in).
  if (args.previewSessionIdOverride) return args.resolvedParentId
  // SPA / drive: prefer the server-resolved served root; fall back to the file-parent
  // only until the session resolves.
  return args.sessionRootId ?? args.resolvedParentId
}
